/**
 * Kismet Quiz V2 — Reveal (results page)
 *
 * Reads the V2 answers from localStorage (`kismet_quiz_v2`) — never from the
 * URL, per the PRD ("Never expose answers in plaintext URLs").
 *
 * Resolver tables encode docs/quiz-v2-resolver-spec.md as reviewed by the
 * client on 2026-07-29: allergen options reduced to None/Chicken/Beef/Fish,
 * gut-route toppers are conditional on allergies. Corrections should be data
 * edits to these tables.
 *
 * Allergens (2026-09-17): what a product contains comes from its
 * `custom.allergens` metafield (chicken / beef / fish, filled by Kismet), passed
 * in through window.quizV2Results.allProducts. Recipe, topper and treat are all
 * checked against it (CATALOG below); the resolver's tables only cover a
 * product with no data.
 */

(function () {
 'use strict';

 var STORAGE_KEY = 'kismet_quiz_v2';

 // Resolver tables + pure functions live in quiz-v2.resolver.js (shared
 // with the wizard, which attaches the outputs to the Klaviyo event).
 var R = window.QuizResolver;
 var RECIPES = R.RECIPES, ROUTES = R.ROUTES;
 var ageMonths = R.ageMonths, realAllergies = R.realAllergies, topGoal = R.topGoal;
 var resolveRoute = R.resolveRoute, resolveRecipe = R.resolveRecipe, resolveTopper = R.resolveTopper, weightBand = R.weightBand;

 var state = {
  dog: null, rec: null, route: null, routeKey: '',
  products: [], cadence: 4, quizId: '', fallbackUsed: false, raw: null
 };

 // ── Helpers ──────────────────────────────────────────────────────────────
 function esc(s) {
  return String(s == null ? '' : s)
   .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
 }

 function getBreedSlug(breed) {
  if (!breed) return '';
  var lower = breed.toLowerCase();
  var paren = lower.match(/^(.+?)\s*\((.+?)\)$/);
  if (paren) {
   return paren[1].trim().replace(/['’]/g, '_').replace(/\s+/g, '_') + '__' +
          paren[2].trim().replace(/['’]/g, '_').replace(/\s+/g, '_');
  }
  var slug = lower.replace(/['’]/g, '_').replace(/\s+/g, '_');
  if (slug === 'poodle') return 'poodle__standard';
  if (slug === 'bulldog') return 'bulldog__american';
  return slug;
 }

 // ── Resolver ─────────────────────────────────────────────────────────────
 // ── Product matching ─────────────────────────────────────────────────────
 function all() { return (window.quizV2Results && window.quizV2Results.allProducts) || []; }

 function isAddOn(title) {
  return /stick|square|shaker|nug|treat/i.test(title);
 }

 // "9" must not match the 9 in "19 lb" — anchor on a word boundary + lb
 function bagRegex(bagWeight) {
  return new RegExp('\\b' + bagWeight + '\\s*lb', 'i');
 }

 function matchesRecipe(p, recipeKey) {
  var t = p.title.toLowerCase();
  // A topper carries "Recipe" in its title and would slip past isAddOn.
  if (isTopper(p)) return false;
  if (isAddOn(t) && !t.includes('recipe') && !t.includes('food')) return false;
  return RECIPES[recipeKey].match.some(function (w) { return t.indexOf(w) > -1; });
 }

 function findRecipe(recipeKey, bagWeight) {
  var re = bagRegex(bagWeight);
  return all().find(function (p) {
   return matchesRecipe(p, recipeKey) && p.variants.some(function (v) { return re.test(v.title); });
  }) || null;
 }

 // The recipe as a product in any bag size (to read its allergens). A bag
 // variant is required so a starter pack titled "... Chicken Dog Food ..."
 // never stands in for the recipe.
 function recipeProduct(recipeKey) {
  return all().find(function (p) {
   return matchesRecipe(p, recipeKey) && p.variants.some(function (v) { return /\blb\b/i.test(v.title); });
  }) || null;
 }

 // Toppers are matched on product type, not on the word "Shakers": the line was
 // renamed to "Kismet Daily <benefit> Formula" (Sep 2026) and the titles no longer
 // carry it. Perk SKUs never reach this list — the section filters them out.
 function isTopper(p) {
  return (p.type || '').toLowerCase() === 'toppers';
 }

 function findShaker(keyword) {
  if (!keyword) return null;
  return all().find(function (p) {
   return isTopper(p) && p.title.toLowerCase().indexOf(keyword) > -1;
  }) || null;
 }

 // ── Allergens ────────────────────────────────────────────────────────────
 // `p.allergens` is the product's custom.allergens metafield (array of
 // chicken / beef / fish), null when the product has no data. Kismet's
 // criteria (Ints, 2026-09-17): salmon oil does not count as fish, beef plasma
 // does count as beef — so the Salmon Sticks/Squares carry all three.
 function productAllergens(p) {
  return Array.isArray(p && p.allergens) ? p.allergens : null;
 }

 // Without metafield data fall back to the product name (v1 behaviour).
 var TITLE_ALLERGEN = { chicken: /chicken/i, beef: /beef/i, fish: /salmon|fish/i };

 function containsAllergen(p, allergies) {
  var list = productAllergens(p);
  return allergies.some(function (a) {
   return list ? list.indexOf(a) > -1 : TITLE_ALLERGEN[a].test(p.title || '');
  });
 }

 // What the resolver reads instead of its tables (quiz-v2.resolver.js).
 var CATALOG = {
  recipeAllergens: function (recipeKey) { return productAllergens(recipeProduct(recipeKey)); },
  topperAllergens: function (keyword) { return productAllergens(findShaker(keyword)); }
 };

 // Treats: Squares under 25 lb, Sticks from 25 lb (P10). Beef flavor by
 // default, salmon as the substitute, never one that contains the dog's
 // allergens. When no stick or square is safe, fall back to a bag of Nugs
 // (beef plasma counts as beef, so every stick and square contains beef; the
 // Chicken & Sweet Potato Nugs are the beef-free treat — Ints, 2026-09-17).
 function findTreat(dog, weightLbs) {
  var type = weightLbs < 25 ? 'square' : 'stick';
  var allergies = realAllergies(dog);
  return safeTreat(type, allergies, ['beef', 'salmon']) ||
         safeTreat('nug', allergies, ['chicken', 'beef']) || null;
 }

 // First allergy-safe treat whose title carries `kind`, preferring flavors in
 // order; a bundle or a pack of many bags (a "4-Pack", "15-Pack") never
 // stands in for the single bag.
 function safeTreat(kind, allergies, flavors) {
  var safe = all().filter(function (p) {
   var t = p.title.toLowerCase();
   return t.indexOf(kind) > -1 && !isTopper(p) && !/pack|bundle/.test(t) && !containsAllergen(p, allergies);
  });
  for (var i = 0; i < flavors.length; i++) {
   var hit = safe.find(function (p) { return p.title.toLowerCase().indexOf(flavors[i]) > -1; });
   if (hit) return hit;
  }
  return safe[0] || null;
 }

 // ── Selling plans (ported from the legacy results page) ──────────────────
 function planWeeks(plan) {
  var sources = (plan.options || []).map(function (o) { return o.value || ''; }).concat([plan.name || '']);
  for (var i = 0; i < sources.length; i++) {
   var v = sources[i].toLowerCase();
   var wm = v.match(/(\d+)\s*week/);
   if (wm) return parseInt(wm[1], 10);
   var dm = v.match(/(\d+)\s*day/);
   if (dm) return Math.round(parseInt(dm[1], 10) / 7);
   if (v.indexOf('month') > -1) return 4;
  }
  return null;
 }

 function sellingPlanFor(product, weeks) {
  if (!product || !product.selling_plan_groups || !product.selling_plan_groups.length) return null;
  var plans = [];
  product.selling_plan_groups.forEach(function (g) {
   g.selling_plans.forEach(function (p) { plans.push({ id: p.id, weeks: planWeeks(p) }); });
  });
  var exact = plans.find(function (p) { return p.weeks === weeks; });
  if (exact) return exact.id;
  var known = plans.filter(function (p) { return p.weeks !== null; });
  if (known.length) {
   // Nearest plan; on a tie prefer the longer one so a 2-week cadence never
   // snaps down to the 1-week plan (KISDEV-468).
   known.sort(function (a, b) {
    return (Math.abs(a.weeks - weeks) - Math.abs(b.weeks - weeks)) || (b.weeks - a.weeks);
   });
   return known[0].id;
  }
  return plans.length ? plans[0].id : null;
 }

 function variantFor(product, bagWeight) {
  if (!product) return null;
  if (bagWeight) {
   var re = bagRegex(bagWeight);
   var sized = product.variants.find(function (v) { return re.test(v.title); });
   if (sized) return sized;
  }
  return product.variants.find(function (v) { return v.available; }) || product.variants[0] || null;
 }

 // ── Analytics (results-page taxonomy, no PII) ────────────────────────────
 function track(payload) {
  if (typeof dataLayer === 'undefined') return;
  payload.quiz_variant = 'v2';
  if (state.quizId) payload.quiz_id = state.quizId;
  payload.timestamp = new Date().toISOString();
  dataLayer.push(payload);
 }

 // ── Build the recommendation ─────────────────────────────────────────────
 function build() {
  var raw;
  try { raw = JSON.parse(localStorage.getItem(STORAGE_KEY)); } catch (e) { raw = null; }
  if (!raw || !raw.dogs || !raw.dogs.length) return false;

  var dog = raw.dogs[0];
  if (!dog.weight || !dog.breed) return false;
  state.quizId = raw.quizId || '';
  state.email = raw.email || '';
  state.vetQuestion = raw.vetQuestion || '';

  var r = R.resolve(dog, CATALOG);
  var calories = r.calories;
  var bagWeight = r.bagWeight;
  var cadence = r.cadence;
  var routeKey = r.routeKey;
  var route = r.route;
  var recipeKey = r.recipeKey;

  state.dog = dog;
  state.route = route;
  state.routeKey = routeKey;
  state.cadence = cadence;
  state.rec = {
   calories: calories,
   bagWeight: bagWeight,
   band: r.band,
   recipeKey: recipeKey
  };
  state.raw = raw;

  // No safe recipe → AskAVet path (P6)
  if (!recipeKey) { state.products = []; return true; }

  var food = findRecipe(recipeKey, bagWeight);

  // P11 fallback: mapped product missing from the catalog → Salmon & Brown
  // Rice at default adult portions (9 lb / every 4 weeks). A fish or beef
  // allergy blocks the salmon fallback → AskAVet instead.
  if (!food) {
   var salmonBlocked = R.recipeBlocked('salmon', realAllergies(dog), CATALOG);
   if (salmonBlocked) { state.products = []; return true; }
   state.fallbackUsed = true;
   state.rec.recipeKey = recipeKey = 'salmon';
   state.rec.bagWeight = bagWeight = '9';
   state.cadence = cadence = 4;
   food = findRecipe('salmon', '9');
   if (!food) { state.products = []; return true; } // catalog truly empty → vet path
  }

  var products = [{ product: food, bagWeight: bagWeight, role: 'food' }];
  var shaker = findShaker(r.topper);
  if (shaker) products.push({ product: shaker, role: 'topper' });
  if (route.treats) {
   var treat = findTreat(dog, parseFloat(dog.weight) || 0);
   if (treat) products.push({ product: treat, role: 'treat' });
  }
  state.products = products;
  return true;
 }

 // P11 fallback vs. Klaviyo: the Completed Quiz event already left the gate
 // with the resolver's recipe/bag/cadence. Dog 1 only, on purpose: the Reveal
 // is single-dog by design (build() reads dogs[0]; "Create plan for another
 // dog" restarts the quiz), so no other dog's props can be out of line.
 // When the catalog forces the fallback, align the PROFILE with what the
 // shopper actually sees, once
 // (flag persisted in the quiz state so reloads don't repeat it). The event
 // itself cannot be edited — documented limitation.
 function reportFallbackOnce() {
  if (!state.fallbackUsed || !state.raw || state.raw.fallbackReported) return;
  var api = klaviyoApi();
  if (!api) return;
  try {
   var props = {
    quiz_fallback_used: true,
    dog_1_recipe: state.rec.recipeKey,
    dog_1_recipe_label: (RECIPES[state.rec.recipeKey] || {}).label || '',
    dog_1_bag_lb: parseInt(state.rec.bagWeight, 10),
    dog_1_cadence_weeks: state.cadence,
    quiz_recipe: state.rec.recipeKey
   };
   if (state.email) props.$email = state.email;
   api.identify(props);
   state.raw.fallbackReported = true;
   localStorage.setItem(STORAGE_KEY, JSON.stringify(state.raw));
  } catch (e) { console.error('[Quiz V2 Reveal] Klaviyo error:', e); }
 }

 // ── Rendering ────────────────────────────────────────────────────────────
 function setText(id, text) {
  var el = document.getElementById(id);
  if (el) el.textContent = text;
 }

 function render() {
  var dog = state.dog;
  var name = dog.name || 'your dog';

  setText('qr2-title', state.route.headline.replace('{name}', name));
  var pron = state.dog.sex === 'male' ? 'his' : state.dog.sex === 'female' ? 'her' : 'their';
  setText('qr2-hero-copy',
   'Keep ' + name + '’s complete plan on track with deliveries personalized to ' + pron + ' breed, weight, and age plus subscribe today to save 30% off your first order.');

  var avatar = document.getElementById('qr2-avatar');
  if (dog.photo) {
   avatar.innerHTML = '<img src="' + esc(dog.photo) + '" alt="">';
  } else if (dog.breed) {
   avatar.innerHTML = '<img src="' + (window.SHOPIFY_CDN_BASE_URL || '') + 'breed_' +
    getBreedSlug(dog.breed) + '_face.png?width=300" alt="" onerror="this.style.display=\'none\'">';
  }

  // Figma (2026-08 redesign): breed / weight / kcal — the route label and
  // portion band moved out of the chips (cadence lives in the card's dropdown)
  var weightLbs = parseFloat(dog.weight);
  document.getElementById('qr2-stats').innerHTML =
   stat(dog.breed, 'Breed') +
   stat((weightLbs || dog.weight) + ' lb', 'Size') +
   stat(state.rec.calories, 'Kcal/Day');

  // Benefit-led callout: route title + ✓ chips. An allergen-free chip rides
  // along when the dog has real allergies — the recipe already excludes them
  // (resolver), so it's a true claim, not decoration.
  setText('qr2-callout-title', state.route.calloutTitle.replace('{name}', name));
  var chips = [state.route.calloutChip];
  var chipAllergies = realAllergies(dog);
  if (chipAllergies.length) {
   chips.push(chipAllergies.map(function (a) {
    return a.charAt(0).toUpperCase() + a.slice(1);
   }).join(' & ') + '-Free Recipe');
  }
  document.getElementById('qr2-callout-chips').innerHTML = chips.map(function (c) {
   return '<span class="qr2-chip">' + esc(c) + '</span>';
  }).join('');

  setText('qr2-picks-intro',
   'Try one of ' + name + '’s personalized picks first, with a tailored subscription or one-time purchase.');

  if (!state.products.length) { renderNoSafeRecipe(name); return; }

  // Bag + cadence line (client 2026-08-05). Weeks come from the selling plan
  // the card will actually select — sellingPlanFor snaps state.cadence to the
  // nearest real plan, so echoing raw state.cadence could contradict the card.
  var sizingPlanId = sellingPlanFor(state.products[0].product, state.cadence);
  var sizingWeeks = null;
  if (sizingPlanId) {
   state.products[0].product.selling_plan_groups.forEach(function (g) {
    g.selling_plans.forEach(function (p) { if (p.id === sizingPlanId) sizingWeeks = planWeeks(p); });
   });
  }
  setText('qr2-callout-sizing',
   'Sized for ' + name + ': ' + state.rec.bagWeight + ' lb bag' +
   (sizingWeeks ? ', delivered every ' + (sizingWeeks === 1 ? 'week' : sizingWeeks + ' weeks') : '') + '.');

  mountMainCard();
  filterPicksRow();

  if (window.matchMedia('(max-width: 767px)').matches) {
   document.getElementById('qr2-sticky').removeAttribute('hidden');
  }
 }

 // Main plan card: clone the recommended food's EXISTING shop-product-card
 // out of the picks row into the gift-box slot. The clone is wrapped in the
 // row's scope class (so the row's scoped CSS applies) + the .landing-picker/
 // .shop-row__track chain, gets unique ids (the card id is suffixed so radio
 // names don't collide with the original), and is re-wired by dispatching
 // landing-product-picker.js's `kismet:product-picker:init` event.
 function mountMainCard() {
  var host = document.getElementById('qr2-main-card');
  if (!host || !state.products.length) return;
  var handle = state.products[0].product.handle;
  var srcCard = document.querySelector('.shop-collection-row-section .shop-card[data-product-handle="' + handle + '"]');
  var scopeEl = srcCard && srcCard.closest('[class*="shop-row-"]');
  if (!srcCard || !scopeEl) {
   console.warn('[Quiz V2 Reveal] main product card not found in the picks row (check the row collection)');
   return;
  }
  var scopeClass = '';
  scopeEl.classList.forEach(function (c) { if (c.indexOf('shop-row-') === 0) scopeClass = c; });
  if (!scopeClass) return;

  var cardId = srcCard.getAttribute('data-block-id') || '';
  var html = srcCard.outerHTML;
  if (cardId) html = html.split(cardId).join(cardId + '-qr2main');

  host.innerHTML =
   '<div class="' + scopeClass + ' qr2-main-scope">' +
    '<div class="landing-picker">' +
     '<div class="shop-row__track" data-track data-single>' + html + '</div>' +
    '</div>' +
   '</div>';
  document.dispatchEvent(new CustomEvent('kismet:product-picker:init', { detail: { root: host } }));

  // The clone arrives with the card's OWN defaults (default_size metafield +
  // the 3-week plan) — apply the resolver's recommendation: bag variant
  // and delivery cadence. Changes are dispatched so the picker recalculates
  // pricing exactly as if the shopper had clicked.
  var variant = variantFor(state.products[0].product, state.rec.bagWeight);
  if (variant) {
   var radio = host.querySelector('[data-size-radio][value="' + variant.id + '"]');
   if (radio && !radio.disabled && !radio.checked) {
    radio.checked = true;
    radio.dispatchEvent(new Event('change', { bubbles: true }));
   }
   var mirror = host.querySelector('[data-size-mirror]');
   if (mirror) mirror.value = String(variant.id);
  }
  var planId = sellingPlanFor(state.products[0].product, state.cadence);
  var freq = host.querySelector('[data-frequency-select]');
  if (freq && planId) {
   freq.value = String(planId);
   if (freq.value === String(planId)) {
    freq.dispatchEvent(new Event('change', { bubbles: true }));
   }
  }

  formatReviewCount(host);
 }

 // Client-approved 2026-08-10: the main card's rating badge reads "4.9 ★ (367)".
 // The Yotpo component renders "(367 reviews)" async (and re-renders after the
 // clone mounts), so watch it and trim the count to just the number.
 function formatReviewCount(host) {
  var el = host.querySelector('kismet-product-rating');
  if (!el) return;
  function apply() {
   var count = el.querySelector('.kpr-count');
   if (!count) return;
   var m = (count.textContent || '').match(/([\d,.]+)/);
   if (!m) return;
   var trimmed = '(' + m[1] + ')';
   if (count.textContent === trimmed) return; // already applied — don't re-trigger the observer
   count.textContent = trimmed;
  }
  apply();
  new MutationObserver(apply).observe(el, { childList: true, subtree: true });
 }

 // Individual picks: the EXISTING shop-collection-row section (added after
 // this one in the template) renders a shop-product-card per collection
 // product — hide every card that isn't part of the recommendation.
 function filterPicksRow() {
  var cards = document.querySelectorAll('.shop-collection-row-section .shop-card');
  if (!cards.length) return;
  var handles = {};
  state.products.forEach(function (entry) { handles[entry.product.handle] = true; });
  var shown = 0;
  cards.forEach(function (card) {
   var keep = !!handles[card.dataset.productHandle];
   card.style.display = keep ? '' : 'none';
   if (keep) shown++;
  });
  // Recommended products missing from the row's collection → hide the whole
  // block instead of showing an empty track (both sections should point to
  // the same curated results collection).
  if (!shown) hidePicksRow();
 }

 function hidePicksRow() {
  document.querySelectorAll('.shop-collection-row-section').forEach(function (el) {
   el.style.display = 'none';
  });
  var intro = document.querySelector('.qr2-picks');
  if (intro) intro.setAttribute('hidden', '');
 }

 function stat(value, label) {
  return '<div class="qr2-stat"><strong>' + esc(value) + '</strong><span>' + esc(label) + '</span></div>';
 }

 // No safe recipe: the page drops ALL commerce (box included) and becomes an
 // Ask-a-Vet conversation — same question field as the email gate, wired to
 // the same Klaviyo property (we already have their email from the gate).
 // Copy is provisional, pending client review.
 function renderNoSafeRecipe(name) {
  document.getElementById('qr2').classList.add('qr2--vet'); // hides commerce modules (CSS)
  hidePicksRow();
  setText('qr2-title', 'Let’s find the right plan for ' + name);
  setText('qr2-hero-copy',
   'With ' + name + '’s allergy mix we’d rather have a veterinarian weigh in than guess.');

  document.getElementById('qr2-main-card').innerHTML =
   '<div class="qr2-card qr2-card-vet">' +
    '<h2>Let’s get ' + esc(name) + ' a vet’s eyes first</h2>' +
    '<p>Tell us about ' + esc(name) + '’s allergies and anything else we should know. ' +
     'A real veterinarian will read it and reply' +
     (state.email ? ' to <b>' + esc(state.email) + '</b>' : ' by email') + '.</p>' +
    '<textarea class="qr2-vet-field" id="qr2-vet-question" maxlength="1000"' +
     ' placeholder="Ask our veterinary team anything">' + esc(state.vetQuestion || '') + '</textarea>' +
    '<button type="button" class="qr2-pill-cta" id="qr2-vet-send">Send to our vet team</button>' +
    '<p class="qr2-vet-alt">Prefer email? Write to ' +
     '<a href="mailto:askavet@kismetpets.com">askavet@kismetpets.com</a></p>' +
   '</div>';

  var send = document.getElementById('qr2-vet-send');
  if (send) send.addEventListener('click', sendVetQuestion);
 }

 // Same hybrid Klaviyo access as the wizard (new onsite object or legacy queue)
 function klaviyoApi() {
  var hasNew = window.klaviyo && typeof window.klaviyo.identify === 'function';
  var hasLegacy = window._learnq && Array.isArray(window._learnq);
  if (!hasNew && !hasLegacy) return null;
  return {
   identify: function (props) {
    if (hasNew) return window.klaviyo.identify(props);
    window._learnq.push(['identify', props]);
   },
   track: function (metric, props) {
    if (hasNew) return window.klaviyo.track(metric, props);
    window._learnq.push(['track', metric, props]);
   }
  };
 }

 function sendVetQuestion() {
  var field = document.getElementById('qr2-vet-question');
  var btn = document.getElementById('qr2-vet-send');
  var question = field ? field.value.trim() : '';
  if (!question) { if (field) field.focus(); return; }

  var api = klaviyoApi();
  if (api) {
   try {
    var props = { quiz_vet_question: question.slice(0, 1000) };
    if (state.email) props.$email = state.email;
    api.identify(props);
    api.track('Asked Vet Question', { source: 'quiz_results_askavet' });
   } catch (e) { console.error('[Quiz V2 Reveal] Klaviyo error:', e); }
  }
  // Free text never goes to the dataLayer — only the fact that it was sent
  track({ event: 'quiz_vet_question_submitted', question_length: question.length });

  if (btn) {
   btn.disabled = true;
   btn.textContent = 'Sent — our vet team will reply soon';
  }
  if (field) field.disabled = true;
 }

 // ── Cart ─────────────────────────────────────────────────────────────────
 function addToCart(items, btn, purchaseType) {
  if (!items.length) return;
  var label = btn ? btn.textContent : '';
  if (btn) { btn.disabled = true; btn.textContent = 'Adding…'; }
  fetch('/cart/add.js', {
   method: 'POST',
   headers: { 'Content-Type': 'application/json' },
   body: JSON.stringify({ items: items, sections: ['cart-ajax'] })
  }).then(function (r) {
   if (!r.ok) throw new Error('cart');
   return r.json();
  }).then(function (data) {
   if (btn) { btn.textContent = 'Added to cart'; }
   track({
    event: 'quiz_results_add_to_cart',
    purchase_type: purchaseType,
    item_count: items.length,
    route: state.routeKey
   });
   document.dispatchEvent(new CustomEvent('ajaxProduct:added', { bubbles: true, detail: { product: data } }));
   setTimeout(function () { if (btn) { btn.disabled = false; btn.textContent = label; } }, 2000);
  }).catch(function () {
   if (btn) { btn.disabled = false; btn.textContent = label; }
   alert('Error adding to cart. Please try again.');
  });
 }

 function planItems(subscribe) {
  return state.products.map(function (entry) {
   var v = variantFor(entry.product, entry.bagWeight);
   if (!v) return null;
   var item = { id: v.id, quantity: 1 };
   if (subscribe) {
    var planId = sellingPlanFor(entry.product, state.cadence);
    if (planId) item.selling_plan = planId;
   }
   return item;
  }).filter(Boolean);
 }

 function bindEvents() {
  document.addEventListener('click', function (e) {
   // Sticky mobile CTA adds the FULL plan (food + topper + treats) as a
   // subscription; the cards handle their own individual add-to-carts.
   var subBtn = e.target.closest('#qr2-add-to-cart-sticky');
   if (subBtn) { addToCart(planItems(true), subBtn, 'subscription'); return; }

   // Figma: "Create plan for another dog +" — serial flow while multi-dog is
   // deferred: clear the stored answers and start a fresh quiz.
   if (e.target.closest('#qr2-another-dog')) {
    try { localStorage.removeItem(STORAGE_KEY); } catch (err) { /* noop */ }
    window.location.href = (window.quizV2Results && window.quizV2Results.quizUrl) || '/pages/quiz';
   }
  });
 }

 // ── Order attribution ────────────────────────────────────────────────────
 // Stamps the cart so every order started from the Reveal carries a quiz
 // marker into Shopify (cart attributes ride the checkout and land on the
 // order as custom attributes). A Shopify Flow turns the `quiz` attribute
 // into an order tag, which is what makes quiz revenue reportable in
 // Analytics (`GROUP BY order_tags`) and filterable in the Orders list —
 // the same pattern the store already uses for Superfiliate.
 //
 // Hooked to `ajaxProduct:added` rather than this file's addToCart() on
 // purpose: the plan card is a cloned shop-product-card and the picks rows
 // are shop-collection-row, so both add to cart through
 // landing-product-picker.js. That listener is the only point all three
 // add-to-cart paths pass through.
 var cartStamped = false;

 function stampQuizCart() {
  if (cartStamped) return;
  cartStamped = true;
  var attributes = { quiz: 'v2' };
  if (state.routeKey) attributes.quiz_route = state.routeKey;
  if (state.quizId) attributes.quiz_id = state.quizId;
  // Fire-and-forget: the cart drawer has already refetched by now and
  // attributes change nothing on screen, so a failure must stay silent.
  fetch('/cart/update.js', {
   method: 'POST',
   headers: { 'Content-Type': 'application/json' },
   body: JSON.stringify({ attributes: attributes })
  }).catch(function () { cartStamped = false; });
 }

 function init() {
  document.body.classList.add('quiz-v2-results-active'); // hides the theme header
  var ok = build();
  if (!ok) {
   // No stored answers → straight to the quiz's first step (replace, so Back
   // doesn't bounce through this page). The load veil stays down meanwhile.
   window.location.replace((window.quizV2Results && window.quizV2Results.quizUrl) || '/pages/quiz?view=quiz-v2');
   return;
  }
  render();
  document.body.classList.add('qr2-ready'); // lift the load veil — layout is final
  reportFallbackOnce();
  bindEvents();
  syncCartEmptyPicks();
  document.addEventListener('ajaxProduct:added', stampQuizCart);
  track({
   event: 'quiz_results_viewed',
   route: state.routeKey,
   recipe: state.rec.recipeKey || 'askavet',
   calories_per_day: state.rec.calories,
   bag_weight_lbs: state.rec.bagWeight,
   delivery_cadence_weeks: state.cadence,
   product_count: state.products.length,
   fallback_used: state.fallbackUsed
  });
 }

 if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
 } else {
  init();
 }

 // The cart drawer's empty state shows the dog's own picks (food first)
 // instead of the store-wide defaults from Custom Header.
 function syncCartEmptyPicks() {
  if (!window.customElements || !state.products.length) return;
  var handles = state.products.slice(0, 2).map(function (entry) { return entry.product.handle; });
  customElements.whenDefined('header-cart-drawer').then(function () {
   var drawer = document.querySelector('header-cart-drawer');
   if (drawer && drawer.setEmptyPicks) drawer.setEmptyPicks(handles);
  });
 }

 window.KismetQuizResultsV2 = { state: state, rebuild: function () { build(); render(); syncCartEmptyPicks(); } };
})();
